// WebSocket 客戶端 — 對應 Unity GameServerHandler + BaseGameService 連線流程。
// 送出 PacketCmd、收 ResponseInfo（以 RequestId 對應），握手後訊息以 AES-CBC + HMAC 加密。
//
// ⚠️ 依還原 proto 實作；未對既有後端做位元組級驗證。
import { logger } from '../core/Logger'
import {
  decryptPacket,
  encryptPacket,
  importAesKey,
  importHmacKey,
  type SessionKeys,
} from './crypto/EncryptionHelper'
import { generateRsaKeyPair } from './crypto/handshake'
import { logRecv, logSend } from './netLog'
import { decodeResponse, encodePacket, type ResponsePacket } from './PacketCodec'
import { gatewayType } from './proto/gatewayProto'
import { getGameToken, isDemoMode } from './urlParams'

const REQUEST_TIMEOUT_MS = 15_000

// Gateway 指令名稱 — 對應 Unity BaseGameService。
export const GatewayCmd = {
  GetAESKey: 'GetAESKey',
  Login: 'Login',
  GetBalance: 'GetBalance',
  GetGameInfo: 'GetGameInfo',
  UpdateDeviceInfo: 'UpdateDeviceInfo',
} as const

export interface ConnectOptions {
  readonly gameName: string
  readonly currency: string
}

interface PendingRequest {
  resolve: (response: ResponsePacket) => void
  reject: (error: unknown) => void
  timer: ReturnType<typeof setTimeout>
}

// GetGameInfo 回傳的遊戲資訊（押注區間 / 單局最高倍數）— 供前端對齊合法投注額。
interface GameInfoRaw {
  BetRange?: string[]
  MaxWin?: number
  GameCode?: string
}

export class GameServerClient {
  private ws: WebSocket | null = null
  private keys: SessionKeys | null = null
  private readonly pending = new Map<string, PendingRequest>()
  private serverBalance = 0n
  private serverCurrency = ''
  private gameBetRange: bigint[] = []
  private gameMaxWin = 0

  get balance(): bigint {
    return this.serverBalance
  }

  /** 伺服器回傳的玩家權威幣別（Login / GetBalance 帶回）；未連線時為空字串。 */
  get currency(): string {
    return this.serverCurrency
  }

  /** GetGameInfo 回傳的合法押注區間（伺服器整數單位）。未連線或失敗時為空。 */
  get betRange(): readonly bigint[] {
    return this.gameBetRange
  }

  /** 單局最高贏分倍數（GetGameInfo）。 */
  get maxWin(): number {
    return this.gameMaxWin
  }

  async connect(url: string, options: ConnectOptions): Promise<void> {
    await this.open(url)
    await this.handshake()
    await this.initSession(options)
  }

  // 送出指令並等待對應 RequestId 的回應。
  async request(cmd: string, data: Uint8Array): Promise<ResponsePacket> {
    const ws = this.ws
    if (ws === null) throw new Error('尚未連線')

    const requestId = crypto.randomUUID()
    logSend(cmd, requestId, data)
    const packet = encodePacket({ cmd, requestId, data })
    const framed = this.keys !== null ? await encryptPacket(packet, this.keys) : packet

    return new Promise<ResponsePacket>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId)
        reject(new Error(`請求逾時：${cmd}`))
      }, REQUEST_TIMEOUT_MS)
      this.pending.set(requestId, { resolve, reject, timer })
      ws.send(framed)
    })
  }

  close(): void {
    this.ws?.close()
    this.ws = null
    this.keys = null
  }

  private open(url: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'
      ws.onopen = () => resolve()
      ws.onerror = () => reject(new Error('WebSocket 連線失敗'))
      ws.onclose = () => this.failAll(new Error('連線已關閉'))
      ws.onmessage = (event) => {
        void this.onBinary(event.data as ArrayBuffer)
      }
      this.ws = ws
    })
  }

  // GetAESKey：送 RSA 公鑰(SPKI DER)，伺服器以 PKCS#1 v1.5 加密 AES 金鑰回傳。握手前為明文。
  private async handshake(): Promise<void> {
    const keyPair = generateRsaKeyPair()
    const response = await this.request(GatewayCmd.GetAESKey, keyPair.publicKeyDer)
    if (response.code !== 0) throw new Error(`GetAESKey 失敗 code=${response.code}`)
    const aesRaw = keyPair.decryptAesKey(response.data)
    this.keys = {
      aesKey: await importAesKey(aesRaw),
      hmacKey: await importHmacKey(aesRaw),
    }
  }

  // UpdateDeviceInfo → Login（或 demo 略過）→ GetGameInfo → GetBalance
  private async initSession(options: ConnectOptions): Promise<void> {
    // 以連線參數幣別作為查詢 hint / fallback；後端回應（Login / GetBalance）帶回時覆蓋成權威值。
    this.serverCurrency = options.currency
    await this.request(GatewayCmd.UpdateDeviceInfo, this.buildDeviceInfo())

    const token = getGameToken()
    if (token !== null && token.length > 0) {
      const login = await this.request(GatewayCmd.Login, this.encode('LoginCmd', { Token: token }))
      if (login.code !== 0) throw new Error(`登入失敗 code=${login.code} ${login.message}`)
      if (login.currency.length > 0) this.serverCurrency = login.currency
    } else if (isDemoMode()) {
      logger.warn('無 token：Demo 模式略過登入')
    } else {
      logger.warn('無 token 且非 demo 模式；略過登入（後端可能拒絕後續請求）')
    }

    const gameInfo = await this.request(
      GatewayCmd.GetGameInfo,
      this.encode('GetGameInfoCmd', { GameName: options.gameName, Currency: options.currency }),
    )
    if (gameInfo.code === 0) this.applyGameInfo(gameInfo.data)
    else logger.warn(`GetGameInfo code=${gameInfo.code} ${gameInfo.message}`)

    const balance = await this.request(
      GatewayCmd.GetBalance,
      this.encode('GetBalanceCmd', { Currency: options.currency }),
    )
    if (balance.code === 0) {
      this.serverBalance = balance.balance
      if (balance.currency.length > 0) this.serverCurrency = balance.currency
    } else logger.warn(`GetBalance code=${balance.code} ${balance.message}`)
  }

  // 解析 GetGameInfo 回應，保存押注區間與最高倍數（後端權威，供前端對齊合法投注額）。
  private applyGameInfo(data: Uint8Array): void {
    try {
      const type = gatewayType('RespGetGameInfo')
      const info = type.toObject(type.decode(data), {
        longs: String,
        defaults: true,
        arrays: true,
      }) as GameInfoRaw
      this.gameBetRange = (info.BetRange ?? []).map((value) => BigInt(value))
      this.gameMaxWin = info.MaxWin ?? 0
      logger.info(
        `GetGameInfo：BetRange=[${this.gameBetRange.join(', ')}] MaxWin=${this.gameMaxWin} GameCode=${info.GameCode ?? ''}`,
      )
    } catch (error) {
      logger.warn('GetGameInfo 解析失敗', error)
    }
  }

  private encode(typeName: string, payload: Record<string, unknown>): Uint8Array {
    const type = gatewayType(typeName)
    return type.encode(type.create(payload)).finish()
  }

  private buildDeviceInfo(): Uint8Array {
    return this.encode('DeviceInfo', {
      Manufacturer: 'web',
      Brand: 'web',
      Resolution: `${window.innerWidth}x${window.innerHeight}`,
      Network: 'wifi',
      SysType: 'Web',
      SysVer: navigator.userAgent.slice(0, 64),
      Browser: 'web',
      BrowserVer: '1',
      DeviceId: crypto.randomUUID(),
      Ver: '0.1.0',
    })
  }

  private async onBinary(buffer: ArrayBuffer): Promise<void> {
    try {
      let payload: Uint8Array = new Uint8Array(buffer)
      if (this.keys !== null) {
        payload = await decryptPacket(payload, this.keys)
      }
      const response = decodeResponse(payload)
      logRecv(response)
      const waiter = this.pending.get(response.requestId)
      if (waiter !== undefined) {
        clearTimeout(waiter.timer)
        this.pending.delete(response.requestId)
        waiter.resolve(response)
      }
    } catch (error) {
      this.failAll(error)
    }
  }

  private failAll(error: unknown): void {
    for (const waiter of this.pending.values()) {
      clearTimeout(waiter.timer)
      waiter.reject(error)
    }
    this.pending.clear()
  }
}
